import {Injectable, OnDestroy} from '@angular/core';
import {environment} from '../environments/environment';
import {HttpClient} from '@angular/common/http';
import {Region} from '../models/region';
import {StorageService} from './storage-service';
import {BehaviorSubject, Subject, takeUntil} from 'rxjs';
import {take} from 'rxjs/operators';
import {AuthService} from './auth.service';

const API = environment.API;

@Injectable({
  providedIn: 'root'
})
export class RegionService implements OnDestroy {
  onDestroy$ = new Subject<boolean>();
  regionSlug$ = new BehaviorSubject<string | null>(null);

  constructor(private http: HttpClient,
              private storage: StorageService,
              private authService: AuthService) {
    this.regionSlug$.pipe(takeUntil(this.onDestroy$)).subscribe(regionSlug => {
      this.storage.add(StorageService.REGION_SLUG, regionSlug);
    });
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  getRegionSlug(): Promise<string> {
    return new Promise((resolve, _) => {

      // Get regionSlug from session
      if (this.regionSlug$.value) {
        return resolve(this.regionSlug$.value);
      }

      // Get regionSlug from local storage
      const savedRegionSlug = this.storage.get('regionSlug');
      if (savedRegionSlug) {
        this.regionSlug$.next(savedRegionSlug);
        return resolve(savedRegionSlug);
      }

      // Get regionSlug from current user's
      const currentUser = this.authService.currentUser$.value;
      if (currentUser) {
        this.regionSlug$.next(currentUser.region);
        return resolve(currentUser.region);
      }

      // Get region from ip address
      this.getByIpAddress().pipe(take(1), takeUntil(this.onDestroy$)).subscribe(region => {
        const regionSlug = region ? region.slug! : 'new-york';
        this.regionSlug$.next(regionSlug);
        return resolve(regionSlug);
      });
    });
  }

  getById(id: string) {
    return this.http.get<Region>(`${API}/api/regions/${id}`);
  }

  getBySlug(slug: string) {
    return this.http.get<Region>(`${API}/api/regions/${slug}/slug`);
  }

  getByIpAddress() {
    return this.http.get<Region>(`${API}/api/regions/ipaddress`);
  }

  findRegions(showInvisible: boolean = false, showExclusive: boolean = false, limit: number = 1000, skip: number = 0) {
    const params = {showInvisible, showExclusive, limit, skip}
    return this.http.get<Region[]>(`${API}/api/regions`, {params});
  }

}
